import { useState, useEffect, useMemo, Fragment } from "react";
import {
  collection, query, where, orderBy, limit, getDocs, startAfter,
} from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// REGISTRO DE ACTIVIDAD — panel de auditoría de la plataforma
//
// Lee `audit_logs`, que es la única colección de auditoría del sistema.
//
// DOS FORMATOS CONVIVEN, y el panel entiende los dos:
//   viejo  { action, user_email, user_name, details }
//   nuevo  { accion, accion_especifica, usuario_email, usuario_nombre,
//            entidad_tipo, entidad_id, entidad_nombre, cambios[], motivo,
//            severidad, obra_nombre, modulo, detalle }
//
// Los registros históricos de HYS y Presupuestos van a conservar el formato
// viejo para siempre (salvo que se migren), así que la compatibilidad no es
// transitoria: es permanente.
// ─────────────────────────────────────────────────────────────────────────────

const PAGINA = 200;

// Etiquetas de los verbos canónicos. Los registros viejos muestran su texto
// crudo, que es lo único que tienen.
const ACCIONES = {
  crear: "Creó", editar: "Editó", eliminar: "Eliminó", anular: "Anuló",
  aprobar: "Aprobó", aprobar_parcial: "Aprobó parcial",
  rechazar: "Rechazó", rechazar_parcial: "Rechazó parcial",
  cerrar: "Cerró", reabrir: "Reabrió",
  asignar: "Asignó", reasignar: "Reasignó",
  conciliar: "Concilió", desconciliar: "Desconcilió",
  emitir: "Emitió", responder: "Respondió",
  importar: "Importó", exportar: "Exportó",
  subir: "Subió", descargar: "Descargó",
  firmar: "Firmó", reportar: "Reportó", notificar: "Notificó",
  login: "Ingresó",
};

const COLOR_SEVERIDAD = {
  critico:    { bg: "#fee2e2", color: "#991b1b", label: "Crítico" },
  importante: { bg: "#fef3c7", color: "#92400e", label: "Importante" },
  info:       { bg: "#f1f5f9", color: "#475569", label: "Info" },
};

// Color por app, generado a partir del nombre para que cualquier app nueva
// tenga el suyo sin tener que tocar este archivo.
const PALETA = [
  { bg: "#e0f2fe", color: "#0369a1" }, { bg: "#fef3c7", color: "#92400e" },
  { bg: "#dcfce7", color: "#166534" }, { bg: "#fee2e2", color: "#991b1b" },
  { bg: "#f3e8ff", color: "#6b21a8" }, { bg: "#ffedd5", color: "#9a3412" },
  { bg: "#ccfbf1", color: "#115e59" },
];
function colorApp(nombre) {
  const s = String(nombre || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
  return PALETA[h % PALETA.length];
}

/** Lee un campo aceptando el nombre nuevo y el viejo. */
const val = (log, nuevo, viejo) => log?.[nuevo] ?? log?.[viejo] ?? null;

function accionTexto(log) {
  const a = val(log, "accion", "action");
  const base = ACCIONES[a] || a || "—";
  const esp = log.accion_especifica;
  // Si la acción específica es igual al verbo, no se repite
  if (!esp || esp === a) return base;
  return `${base} · ${String(esp).replace(/_/g, " ")}`;
}

function usuarioTexto(log) {
  return val(log, "usuario_nombre", "user_name") ||
    val(log, "usuario_email", "user_email") || "—";
}

/** Resumen de una línea para la columna Detalle. */
function detalleTexto(log) {
  if (log.entidad_nombre) return log.entidad_nombre;
  const a = val(log, "accion", "action");
  if (a === "login") {
    return `Acceso: ${val(log, "usuario_email", "user_email") || "—"}`;
  }
  const d = log.detalle || log.details;
  if (d && typeof d === "object") {
    return d.nombre || d.email || d.descripcion || "—";
  }
  return typeof d === "string" ? d : "—";
}

const fechaISO = (log) => {
  const ts = log.timestamp?.toDate ? log.timestamp.toDate() : null;
  return ts ? ts.toISOString().split("T")[0] : null;
};

export default function AuditoriaPanel({ isSuperadmin, userProfile, tenants = [] }) {
  const [logs, setLogs] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [ultimoDoc, setUltimoDoc] = useState(null);
  const [hayMas, setHayMas] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const [filtro, setFiltro] = useState({
    app: "", tenant: "", severidad: "", texto: "",
    fechaDesde: "", fechaHasta: "",
  });

  async function cargar(siguiente = false) {
    setCargando(true);
    try {
      const partes = [collection(db, "audit_logs")];
      if (!isSuperadmin) {
        partes.push(where("tenant_id", "==", userProfile?.tenant_id));
      }
      partes.push(orderBy("timestamp", "desc"));
      if (siguiente && ultimoDoc) partes.push(startAfter(ultimoDoc));
      partes.push(limit(PAGINA));

      const snap = await getDocs(query(...partes));
      const nuevos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLogs(siguiente ? [...logs, ...nuevos] : nuevos);
      setUltimoDoc(snap.docs[snap.docs.length - 1] || null);
      setHayMas(snap.docs.length === PAGINA);
    } catch (e) {
      console.error("Error cargando audit logs:", e);
    }
    setCargando(false);
  }

  // La primera carga sólo depende de si es superadmin (cambia el filtro por
  // tenant). `cargar` se redefine en cada render, así que no va como dependencia.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(false) }, [isSuperadmin]);

  // Las apps del filtro salen de los propios registros: así una app nueva
  // aparece sola, sin tener que tocar este archivo.
  const appsPresentes = useMemo(
    () => [...new Set(logs.map((l) => l.app).filter(Boolean))].sort(),
    [logs]);

  const visibles = useMemo(() => logs.filter((l) => {
    if (filtro.app && l.app !== filtro.app) return false;
    if (filtro.tenant && l.tenant_id !== filtro.tenant) return false;
    if (filtro.severidad && (l.severidad || "info") !== filtro.severidad) return false;
    if (filtro.fechaDesde || filtro.fechaHasta) {
      const f = fechaISO(l);
      if (f) {
        if (filtro.fechaDesde && f < filtro.fechaDesde) return false;
        if (filtro.fechaHasta && f > filtro.fechaHasta) return false;
      }
    }
    if (filtro.texto) {
      const q = filtro.texto.toLowerCase();
      const campos = [
        accionTexto(l), usuarioTexto(l), l.entidad_nombre, l.entidad_id,
        l.modulo, l.obra_nombre, l.motivo,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!campos.includes(q)) return false;
    }
    return true;
  }), [logs, filtro]);

  const inputS = {
    padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0",
    fontSize: 13, fontFamily: "inherit",
  };
  const thS = {
    padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#475569",
    whiteSpace: "nowrap",
  };
  const tdS = { padding: "10px 12px", color: "#334155", verticalAlign: "top" };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16,
        flexWrap: "wrap", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          Registro de actividad
        </h3>

        <input placeholder="Buscar entidad, usuario, motivo..." style={{ ...inputS, minWidth: 230 }}
          value={filtro.texto}
          onChange={(e) => setFiltro((f) => ({ ...f, texto: e.target.value }))} />

        <select style={inputS} value={filtro.app}
          onChange={(e) => setFiltro((f) => ({ ...f, app: e.target.value }))}>
          <option value="">Todas las apps</option>
          {appsPresentes.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <select style={inputS} value={filtro.severidad}
          onChange={(e) => setFiltro((f) => ({ ...f, severidad: e.target.value }))}>
          <option value="">Toda severidad</option>
          <option value="critico">Sólo críticos</option>
          <option value="importante">Importantes</option>
          <option value="info">Info</option>
        </select>

        {isSuperadmin && (
          <select style={inputS} value={filtro.tenant}
            onChange={(e) => setFiltro((f) => ({ ...f, tenant: e.target.value }))}>
            <option value="">Todas las empresas</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}

        <input type="date" style={inputS} value={filtro.fechaDesde}
          onChange={(e) => setFiltro((f) => ({ ...f, fechaDesde: e.target.value }))} />
        <input type="date" style={inputS} value={filtro.fechaHasta}
          onChange={(e) => setFiltro((f) => ({ ...f, fechaHasta: e.target.value }))} />

        <button onClick={() => cargar(false)}
          style={{ ...inputS, cursor: "pointer", background: "#fff", fontWeight: 600 }}>
          ↻ Actualizar
        </button>
      </div>

      {cargando && logs.length === 0 ? (
        <p style={{ color: "#64748b", fontSize: 13 }}>Cargando...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          {/* minWidth fuerza el scroll horizontal: sin él las celdas se
              comprimen y las últimas columnas quedan cortadas. */}
          <table style={{ width: "100%", minWidth: isSuperadmin ? 1020 : 880,
            borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={thS}>Fecha</th>
                <th style={thS}>App</th>
                <th style={{ ...thS, width: 150 }}>Acción</th>
                <th style={{ ...thS, minWidth: 200 }}>Sobre qué</th>
                <th style={{ ...thS, width: 190 }}>Usuario</th>
                {isSuperadmin && <th style={{ ...thS, width: 100 }}>Empresa</th>}
                <th style={{ ...thS, width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((log) => {
                const sev = COLOR_SEVERIDAD[log.severidad] || COLOR_SEVERIDAD.info;
                const ca = colorApp(log.app);
                const tieneDetalle = (log.cambios?.length > 0) || log.motivo ||
                  log.detalle || log.details;
                const open = abierto === log.id;
                return (
                  <Fragment key={log.id}>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ ...tdS, color: "#64748b", whiteSpace: "nowrap" }}>
                        {log.timestamp?.toDate
                          ? log.timestamp.toDate().toLocaleString("es-AR")
                          : "—"}
                      </td>
                      <td style={tdS}>
                        <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11,
                          fontWeight: 600, background: ca.bg, color: ca.color,
                          whiteSpace: "nowrap" }}>
                          {log.app || "—"}
                        </span>
                        {log.modulo && (
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                            {log.modulo}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdS, fontSize: 12 }}>
                        {accionTexto(log)}
                        {log.severidad && log.severidad !== "info" && (
                          <span style={{ marginLeft: 6, padding: "1px 6px",
                            borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: sev.bg, color: sev.color }}>
                            {sev.label}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdS, fontSize: 12 }}>
                        {detalleTexto(log)}
                        {log.entidad_tipo && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>
                            {log.entidad_tipo}
                          </div>
                        )}
                        {log.obra_nombre && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>
                            📍 {log.obra_nombre}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdS, fontSize: 12, wordBreak: "break-word" }}>
                        {usuarioTexto(log)}
                      </td>
                      {isSuperadmin && (
                        <td style={{ ...tdS, color: "#64748b", fontSize: 12,
                          whiteSpace: "nowrap" }}>
                          {log.tenant_id}
                        </td>
                      )}
                      <td style={{ ...tdS, whiteSpace: "nowrap", textAlign: "right" }}>
                        {tieneDetalle && (
                          <button onClick={() => setAbierto(open ? null : log.id)}
                            style={{ background: "none", border: "none", cursor: "pointer",
                              color: "#0369a1", fontSize: 12, fontWeight: 600,
                              padding: 0, fontFamily: "inherit" }}>
                            {open ? "▾ Ocultar" : "▸ Ver"}
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* El antes/después es lo que pide un auditor y lo que el
                        formato viejo no tenía. */}
                    {open && (
                      <tr>
                        <td colSpan={isSuperadmin ? 7 : 6}
                          style={{ padding: "12px 16px 18px", background: "#f8fafc" }}>
                          {log.motivo && (
                            <p style={{ fontSize: 12, marginBottom: 10 }}>
                              <strong>Motivo:</strong> {log.motivo}
                            </p>
                          )}

                          {log.cambios?.length > 0 && (
                            <table style={{ borderCollapse: "collapse", marginBottom: 12,
                              fontSize: 12, minWidth: 420 }}>
                              <thead>
                                <tr>
                                  <th style={{ ...thS, padding: "4px 10px", fontSize: 10 }}>Campo</th>
                                  <th style={{ ...thS, padding: "4px 10px", fontSize: 10 }}>Antes</th>
                                  <th style={{ ...thS, padding: "4px 10px", fontSize: 10 }}>Después</th>
                                </tr>
                              </thead>
                              <tbody>
                                {log.cambios.map((c, i) => (
                                  <tr key={i} style={{ borderTop: "1px solid #e2e8f0" }}>
                                    <td style={{ padding: "5px 10px", fontWeight: 600 }}>
                                      {c.campo}
                                    </td>
                                    <td style={{ padding: "5px 10px", color: "#991b1b" }}>
                                      {c.antes === null || c.antes === undefined
                                        ? "—" : String(c.antes)}
                                    </td>
                                    <td style={{ padding: "5px 10px", color: "#166534" }}>
                                      {c.despues === null || c.despues === undefined
                                        ? "—" : String(c.despues)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {(log.detalle || log.details) && (
                            <pre style={{ fontSize: 11, color: "#475569", margin: 0,
                              whiteSpace: "pre-wrap", wordBreak: "break-word",
                              background: "#fff", padding: "8px 10px", borderRadius: 6,
                              border: "1px solid #e2e8f0", maxHeight: 260,
                              overflow: "auto" }}>
                              {JSON.stringify(log.detalle || log.details, null, 2)}
                            </pre>
                          )}

                          <p style={{ fontSize: 10, color: "#94a3b8", marginTop: 8 }}>
                            {log.entidad_id && `ID: ${log.entidad_id}`}
                            {log.usuario_rol && ` · rol al momento: ${log.usuario_rol}`}
                            {log.origen && ` · origen: ${log.origen}`}
                          </p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {visibles.length === 0 && (
            <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: 24 }}>
              No hay registros para mostrar.
            </p>
          )}

          {/* Sin paginado, los registros de las apps con más movimiento tapan
              a las demás apenas se llena el primer lote. */}
          {hayMas && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <button onClick={() => cargar(true)} disabled={cargando}
                style={{ ...inputS, cursor: "pointer", background: "#fff",
                  fontWeight: 600, padding: "8px 20px" }}>
                {cargando ? "Cargando..." : `Cargar ${PAGINA} más`}
              </button>
              <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                Mostrando {visibles.length} de {logs.length} cargados
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
